import { UseFieldConfig } from 'react-final-form'
import { usePatchedField } from 'stores/utils/hooks/usePatchedField'
import { FilledInputProps, TextFieldProps } from '@mui/material'
import { convertToInteger, formatToDeAmount } from 'utils/number'
import { useAmount } from '@finanzcheck/catalyst-renderless-components'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import {
  useCallback,
  useMemo,
  useState,
  ClipboardEvent,
  memo,
  useEffect
} from 'react'
import { MINIMUM_VEHICLE_AMOUNT_VALUE } from 'formInitialValues'
import {
  PATH_AMOUNT_INPUT_EXTENDED_MAX_LENGTH,
  PATH_AMOUNT_INPUT_MAX_LENGTH
} from 'constants/index'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import WithIdleFieldChange from '../WithIdleFieldChange'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import Text, { Unit } from '../Input/Text'

const MINIMUM_VEHICLE_AMOUNT_HINT = `Der Kaufpreis muss mindestens ${formatToDeAmount(
  MINIMUM_VEHICLE_AMOUNT_VALUE
)} betragen.`

interface OwnProps {
  unit?: Unit
  clue?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig?: UseFieldConfig<any>
  outerLabel?: boolean
  smallTextField?: boolean
  showTooltipOnFieldClick?: boolean
  isTooltipHidden?: boolean
  isVehiclePriceCalculated?: boolean
  scrollOnFocusEnabled?: boolean
  label?: string
  tooltip?: string
  touchIgnored?: boolean
}

type Props = Exclude<PathFormFieldProps<number>, 'onChangeConstraints'> &
  OwnProps &
  Pick<TextFieldProps, 'InputLabelProps'>

const AmountInput = observer(
  ({
    path,
    unit,
    clue,
    onChangeConstraints,
    onTooltip,
    onChange,
    onBlur,
    onFocus,
    fieldConfig,
    outerLabel,
    smallTextField,
    showTooltipOnFieldClick,
    isTooltipHidden,
    isVehiclePriceCalculated,
    scrollOnFocusEnabled,
    label: labelProp,
    tooltip: tooltipProp,
    touchIgnored
  }: Props) => {
    const { input, meta, error } = usePatchedField(
      path,
      {
        ...fieldConfig,
        parse: (v: number) => {
          const stringValue = `${v}`
          if (stringValue.length === 0) {
            return 0
          }

          return +stringValue.replace(/\D/g, '')
        }
      },
      path !== 'common.vehicle.initialPayment'
    )

    const {
      traversal,
      traversal: {
        removeErrorsForPath,
        data,
        handlePathFormFieldChange,
        handleMultiplePathFormFieldChanges
      },
      traversalSession: {
        setInitialPaymentErrorTriggered,
        initialPaymentErrorTriggered
      }
    } = useStores()

    const [pasteError, setPasteError] = useState<string | undefined>(undefined)
    const [minimumVehicleAmountHint, setMinimumVehicleAmountHint] =
      useState<string>('')

    const focusHandler = useCallback(
      (value: string) => {
        input.onFocus()
        if (onFocus) {
          onFocus(+value, convertToInteger(value).value || 0)
        }
        setInitialPaymentErrorTriggered(false)
      },
      [input, onFocus, setInitialPaymentErrorTriggered]
    )
    const blurHandler = useCallback(
      (value: string) => {
        if (
          path === 'common.vehicle.price' &&
          value &&
          convertToInteger(value)?.value !== undefined &&
          convertToInteger(value).value! < MINIMUM_VEHICLE_AMOUNT_VALUE
        ) {
          handleMultiplePathFormFieldChanges(
            [
              {
                path: 'common.vehicle.price',
                value: MINIMUM_VEHICLE_AMOUNT_VALUE
              },
              {
                path: 'common.amount',
                value: MINIMUM_VEHICLE_AMOUNT_VALUE
              }
            ],
            true
          )
          setMinimumVehicleAmountHint(MINIMUM_VEHICLE_AMOUNT_HINT)
        }
        input.onBlur()
        if (onBlur) {
          onBlur(+value, convertToInteger(value).value || 0)
        }
      },
      [input, onBlur, handleMultiplePathFormFieldChanges, path]
    )

    const changeHandler = useCallback(
      (value: string) => {
        if (minimumVehicleAmountHint) {
          setMinimumVehicleAmountHint('')
        }

        const valueInteger = convertToInteger(value).value || 0

        if (
          isVehiclePriceCalculated &&
          traversal.data.common.purpose === 'PRE_OWNED_CAR'
        ) {
          const getCalculatedAmount = (): number => {
            if (path === 'common.vehicle.initialPayment') {
              return data.common.vehicle.price - valueInteger
            }

            if (path === 'common.vehicle.price') {
              return valueInteger - (data.common.vehicle.initialPayment || 0)
            }

            return 0
          }

          const calculatedAmount = getCalculatedAmount()
          if (calculatedAmount > 0) {
            handlePathFormFieldChange(
              `common.amount`,
              calculatedAmount,
              calculatedAmount > MINIMUM_VEHICLE_AMOUNT_VALUE
            )
          }
        }

        if (path === 'common.amount') {
          handleMultiplePathFormFieldChanges([
            {
              path: 'common.amount',
              value: valueInteger
            },
            {
              path: 'common.purpose',
              value: traversal.data.common.purpose
            }
          ])
        }

        input.onChange(value)
        if (onChange) {
          onChange(+value, convertToInteger(value).value || 0)
        }

        setPasteError(undefined)
        removeErrorsForPath(path)
      },
      [
        traversal.data.common.purpose,
        data.common.vehicle.price,
        data.common.vehicle.initialPayment,
        path,
        input,
        onChange,
        removeErrorsForPath,
        handlePathFormFieldChange,
        handleMultiplePathFormFieldChanges,
        isVehiclePriceCalculated,
        minimumVehicleAmountHint
      ]
    )

    const maxLength =
      path === 'common.amount'
        ? PATH_AMOUNT_INPUT_EXTENDED_MAX_LENGTH
        : PATH_AMOUNT_INPUT_MAX_LENGTH

    const inputProps: FilledInputProps['inputProps'] = useMemo(
      () => ({
        inputMode: 'numeric',
        type: 'tel',
        maxLength
      }),
      []
    )

    const pasteHandler = useCallback(
      (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()

        const original = event.clipboardData.getData('Text')

        const { complete, value = 0 } = convertToInteger(original)

        if (!complete) {
          setPasteError(
            `Vorsicht: Ihre Eingabe "${original}" enthielt ungültige Zeichen. Es sind nur ganze Zahlen und keine Sonderzeichen erlaubt.`
          )
        }

        input.onChange(value)

        // eslint-disable-next-line no-param-reassign,@typescript-eslint/no-explicit-any
        ;(event.target as any).selectionStart = inputProps.maxLength // the selection should always be at the end after pasting
      },
      [input, setPasteError, inputProps.maxLength]
    )

    const { getProps, ref } = useAmount({ value: input.value })

    useEffect(() => {
      // traversal model in imod's backend services does not offer the option
      // to store the initial price so we need to calculate it on first render
      // if not present, e.g. from params
      if (path === 'common.vehicle.initialPayment') {
        const { initialPayment, price } = data.common.vehicle
        if (initialPayment || initialPayment === 0) {
          setInitialPaymentErrorTriggered(false)
          return
        }

        const calculatedInitialPayment = price - data.common.amount
        handlePathFormFieldChange(
          'common.vehicle.initialPayment',
          calculatedInitialPayment,
          false
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, setInitialPaymentErrorTriggered])

    const generateCarLoanHint = () => {
      if (
        initialPaymentErrorTriggered &&
        path === 'common.vehicle.initialPayment'
      ) {
        const formattedPrice = formatToDeAmount(data.common.vehicle.price)
        const formattedMaxInitialPayment = formatToDeAmount(
          data.common.vehicle.price - MINIMUM_VEHICLE_AMOUNT_VALUE
        )

        return `Bei einem Preis von ${formattedPrice} darf die Anzahlung höchstens ${formattedMaxInitialPayment} betragen.`
      }

      return ''
    }

    const carLoanHint = generateCarLoanHint()

    const { label, placeholder, tooltip } = useMeta(path)
    const { id, name, type } = getProps({ name: path, id: path })

    const numberInput = (
      <Text
        ref={ref}
        label={labelProp ?? label}
        placeholder={placeholder}
        tooltip={tooltipProp ?? tooltip}
        id={id}
        name={name || input.name}
        type={type}
        onPaste={pasteHandler}
        onFocus={focusHandler}
        onBlur={blurHandler}
        onChange={changeHandler}
        error={meta.touched || touchIgnored ? error || pasteError : ''}
        unit={unit}
        clue={clue || carLoanHint || minimumVehicleAmountHint}
        inputProps={inputProps}
        onTooltip={onTooltip}
        path={path}
        outerLabel={outerLabel}
        smallTextField={smallTextField}
        showTooltipOnFieldClick={showTooltipOnFieldClick}
        isTooltipHidden={isTooltipHidden}
        scrollOnFocusEnabled={scrollOnFocusEnabled}
      />
    )

    if (onChangeConstraints) {
      return (
        <WithIdleFieldChange delayInMs={onChangeConstraints.delayInMs}>
          {numberInput}
        </WithIdleFieldChange>
      )
    }

    return numberInput
  }
)

const PathAmountInput = (props: Props) => {
  const {
    path,
    labelPath,
    unit,
    clue,
    onChangeConstraints,
    onTooltip,
    onChange,
    onBlur,
    onFocus,
    fieldConfig,
    outerLabel,
    smallTextField,
    showTooltipOnFieldClick,
    isTooltipHidden,
    isVehiclePriceCalculated,
    scrollOnFocusEnabled,
    label,
    tooltip,
    touchIgnored
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <WithFieldInteraction path={path}>
        <AmountInput
          path={path}
          labelPath={labelPath}
          unit={unit}
          clue={clue}
          onChangeConstraints={onChangeConstraints}
          onTooltip={onTooltip}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          fieldConfig={fieldConfig}
          outerLabel={outerLabel}
          smallTextField={smallTextField}
          showTooltipOnFieldClick={showTooltipOnFieldClick}
          isTooltipHidden={isTooltipHidden}
          isVehiclePriceCalculated={isVehiclePriceCalculated}
          scrollOnFocusEnabled={scrollOnFocusEnabled}
          label={label}
          touchIgnored={touchIgnored}
          tooltip={tooltip}
        />
      </WithFieldInteraction>
    </FieldDataCollector>
  )
}

export default memo(PathAmountInput)
